/**
 * Module to manage plugins
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('./constants/action_types');
var di = require('./di');
var enums = require('./constants/enums');
var EventEmitter = require('./event_emitter');
var Flux = require('./flux');

/**
 * Registers an API module.
 * @param {string} name API module name.
 * @param {Object|Getter} module API module or getter
 */
exports.registerApiModule = function(name, module) {
  if (_.isArray(module)) {
    // The module is a getter; use di.
    module = di.evaluate(module);
  }

  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.apiModules,
    name: name,
    plugin: module
  });
};

/**
 * Registers a value with the DependencyInjection system
 * @param {string} id that will be prefixed with 'sources/'
 * @param {*} dep
 */
exports.registerDependency = function(id, dep) {
  var existing = di.get(id);

  if (__INVARIANT__ && existing && dep !== existing) {
    throw new Error('A different dependency is already registered for ' + id);
  }

  if (!existing) {
    di.register(id, dep);
  }
};

/**
 * @param {VisitorProfileProvider} provider
 */
exports.registerVisitorProfileProvider = function(provider) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.visitorProfileProviders,
    name: provider.provides,
    plugin: provider
  });
};

/**
 * @typedef {Object} ViewDataProvider
 */

/**
 * @param {ViewDataProvider} provider
 */
exports.registerViewProvider = function(provider) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.viewProviders,
    name: provider.provides,
    plugin: provider
  });
};

/**
 * Registers a matcher with a condition type
 * @param {string} conditionType ex: 'time_and_day' or 'behavioral'
 * @param {AudienceMatcher} matcher
 */
exports.registerAudienceMatcher = function(conditionType, matcher) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.audienceMatchers,
    name: conditionType,
    plugin: matcher
  });
};

/**
 * @typedef {Object} ViewMatcher
 */

/**
 * Registers a matcher with a condition type
 * @param {string} conditionType ex: 'time_and_day' or 'behavioral'
 * @param {ViewMatcher} matcher
 */
exports.registerViewMatcher = function(conditionType, matcher) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.viewMatchers,
    name: conditionType,
    plugin: matcher
  });
};

/**
 * Registers an AnalyticsTracker
 * @param {AnalyticsTracker} tracker
 * @param {string} name
 */
exports.registerAnalyticsTracker = function(name, tracker) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.analyticsTrackers,
    name: name,
    plugin: tracker
  });
};

/**
 * Registers an ViewTagLocator function
 * @param {string} locatorType
 * @param {Function} locatorFn
 */
exports.registerViewTagLocator = function(locatorType, locatorFn) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.viewTagLocators,
    name: locatorType,
    plugin: locatorFn
  });
};

/**
 * Register an audience config for a vendor, with provider, matcher, and (optional) dependencies. Automatically
 * name the provided field `"vendor.{{config.vendor}}"` and limit the `fieldsNeeded` to only those supplied by the plugin.
 *
 * @param {Object} config
 * @param {string} config.vendor
 * @param {string|Function|VisitorProfileProvider} config.provider - VisitorProfileProvider or "factory" string/function
 *  * If a string is provided, expect that module to return a VisitorProfileProvider factory function when resolved with di.get.
 *  * If a function, expect that function to return a VisitorProfileProvider when called with config.vendor.
 * @param {string|Function} config.matcher
 *  * If a string is provided, it is assumed to be a DI id, resolved with di.get.
 *    (used at least by custom audience integrations).
 *  * If a function is provided, it will be used as the matcher.
 * @param {Object} [config.dependencies]
 */
exports.registerAudiencePlugin = function(config) {
  if (__INVARIANT__) {
    if (!config.vendor) {
      throw new Error('config.vendor is required!');
    }

    if (!config.provider) {
      throw new Error('config.provider is required!');
    }

    if (!config.matcher) {
      throw new Error('config.matcher is required!');
    }
  }

  // TODO(tyler): remove this, plugins should use require for own deps
  if (config.dependencies) {
    _.each(config.dependencies, function(dependency, depName) {
      exports.registerDependency(depName, dependency);
    });
  }

  var provided = 'vendor.' + config.vendor;

  var provider;
  if (_.isString(config.provider)) {
    provider = di.get(config.provider)(config.vendor);
  } else if (_.isFunction(config.provider)) {
    provider = config.provider(config.vendor);
  } else {
    provider = _.cloneDeep(config.provider);
  }

  exports.registerVisitorProfileProvider(_.extend(provider, {
    provides: provided,
  }));

  var match;
  // TODO(tyler): maybe support getter with di.evaluate ("with great power...")
  if (_.isString(config.matcher)) {
    match = di.get(config.matcher);
  } else {
    match = config.matcher;
  }

  // TODO(tyler): maybe do this first to catch collisions?
  var matcher = {
    fieldsNeeded: [provided],
    match: function(value, cond) {
      return match(value[provided], cond);
    }
  };

  exports.registerAudienceMatcher(provided, matcher);
};

/**
 * Object providing access to tokens for removing showWidget/hideWidget handlers
 * @typedef {Object} RegisterWidgetResult
 * @property {string} showToken
 * @property {string} hideToken
 */

/**
 * Configuration for a widget
 * @typedef {Object} WidgetConfig
 * @property {string} widgetId ID of widget to be registered
 * @property {Function} showFn Event handler for 'showWidget'
 * @property {Function} hideFn Event handler for 'hideWidget'
 */

/**
 * Registers event handlers for 'showWidget' and 'hideWidget' events
 * @param {WidgetConfig|Array} config Widget config, or getter that evaluates to
 * a WidgetConfig
 * @returns {RegisterWidgetResult} An object with showToken and hideToken
 * properties, which can be used to remove the added event handlers
 */
exports.registerWidget = function(config) {
  if (_.isArray(config)) {
    config = di.evaluate(config);
  }
  var showToken = EventEmitter.on({
    filter: {
      type: 'showWidget',
      name: config.widgetId,
    },
    handler: config.showFn,
  });
  var hideToken = EventEmitter.on({
    filter: {
      type: 'hideWidget',
      name: config.widgetId,
    },
    handler: config.hideFn,
  });
  return {
    showToken: showToken,
    hideToken: hideToken,
  };
};

/**
 * Registers applier for a type of change
 * @param {string} changeType
 * @param {ChangeApplier} changeApplier
 */
exports.registerChangeApplier = function(changeType, changeApplier) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.changeAppliers,
    name: changeType,
    plugin: changeApplier
  });
};

/**
 * Registers decider
 * @param {string} deciderType
 * @param {Decider} decider
 */
exports.registerDecider = function(deciderType, decider) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.deciders,
    name: deciderType,
    plugin: decider
  });
};

/**
 * Registers a new event extension
 * @param {string} eventType
 * @param {EventImplementation} implementation
 */
exports.registerEventImplementation = function(eventType, implementation) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.eventImplementations,
    name: eventType,
    plugin: implementation,
  });
};

/**
 * @typedef {Object} ViewTrigger
 */

/**
 * Registers a view trigger
 * @param {string} triggerType
 * @param {ViewTrigger} [trigger]
 */
exports.registerViewTrigger = function(triggerType, trigger) {
  Flux.dispatch(actionTypes.REGISTER_PLUGIN, {
    type: enums.PluginTypes.viewTriggers,
    name: triggerType,
    plugin: trigger,
  });
};

